import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ScanSearch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { num } from '../logistics/format';
import { DataTable } from './DataTable';
import { useIsMobile } from './useIsMobile';

export const QUADS = {
  estrelas: { label: 'Estrelas', color: '#16a34a', help: 'muito visto e muito convertido' },
  muito_visto_pouco_vendido: { label: 'Muito visto, pouco vendido', color: '#dc2626', help: 'atrai visitas, mas converte abaixo da mediana' },
  pouco_visto_muito_convertido: { label: 'Pouco visto, muito convertido', color: '#2563eb', help: 'converte bem, merece mais exposição' },
  baixo_desempenho: { label: 'Baixo desempenho', color: '#94a3b8', help: 'pouco visto e pouco convertido' },
};
const rate = (v) => (v == null ? '—' : `${v.toFixed(2).replace('.', ',')}%`);

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover p-2 text-xs shadow-md max-w-[16rem]">
      <p className="font-medium">{p.nome}</p>
      <p className="text-muted-foreground">{QUADS[p.quadrante].label}</p>
      <p>{num(p.views)} visualizações · {num(p.carts)} no carrinho · {num(p.purchases)} compras</p>
      <p>Taxa de compra: {rate(p.taxa_compra)}</p>
    </div>
  );
};

const Quadrant = ({ v }) => (
  <Card className="min-w-0">
    <CardContent className="p-4">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2" data-testid="quadrant-legend">
        {Object.entries(QUADS).map(([k, q]) => (
          <span key={k} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: q.color }} />{q.label}</span>
        ))}
      </div>
      <div style={{ width: '100%', height: 340 }} data-testid="quadrant-chart">
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis type="number" dataKey="views" name="Visualizações" scale="log" domain={['auto', 'auto']} tick={{ fontSize: 11 }}
              label={{ value: 'Visualizações (escala log)', position: 'insideBottom', offset: -18, fontSize: 11 }} />
            <YAxis type="number" dataKey="taxa_compra" name="Taxa de compra" unit="%" tick={{ fontSize: 11 }}
              label={{ value: 'Taxa de compra', angle: -90, position: 'insideLeft', fontSize: 11 }} />
            <Tooltip content={<Tip />} />
            <ReferenceLine x={v.median_views} stroke="#64748b" strokeDasharray="4 3" />
            <ReferenceLine y={v.median_rate} stroke="#64748b" strokeDasharray="4 3" />
            {Object.entries(QUADS).map(([k, q]) => (
              <Scatter key={k} data={v.scatter.filter((p) => p.quadrante === k)} fill={q.color} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-muted-foreground">Linhas tracejadas: medianas da loja ({num(v.median_views)} visualizações e {rate(v.median_rate)} de taxa de compra). Só produtos com {v.min_views}+ visualizações.</p>
    </CardContent>
  </Card>
);

const AnalyzeBtn = ({ url, api }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!url || !api?.createDiagnostic) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.createDiagnostic(url); navigate(`/diagnostics/${data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível abrir o diagnóstico'); setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={go} disabled={busy} data-testid="analyze-page-btn">
      {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ScanSearch className="h-3 w-3 mr-1" />}Analisar página
    </Button>
  );
};

const ShortList = ({ k, title, rows, metric, api, testId }) => (
  <Card className="min-w-0" data-testid={testId}>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{title}</p>
      {k && <p className="text-[11px] text-muted-foreground mb-2">Quadrante: <span style={{ color: QUADS[k].color }}>{QUADS[k].label}</span></p>}
      <ul className="space-y-2 mt-2">
        {rows.map((r) => (
          <li key={r.key} className="text-sm">
            <p className="font-medium leading-snug">{r.nome}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{metric(r)}</span>
              {k === 'muito_visto_pouco_vendido' && <AnalyzeBtn url={r.url_produto} api={api} />}
            </div>
          </li>
        ))}
        {!rows.length && <li className="text-sm text-muted-foreground">Nenhum produto nesta situação.</li>}
      </ul>
    </CardContent>
  </Card>
);

const COLS = [
  { key: 'nome', label: 'Produto', wrap: true, render: (r) => <span>{r.nome}{!r.matched && <span className="ml-1 text-[10px] text-muted-foreground">(nome do GA4)</span>}</span> },
  { key: 'views', label: 'Visualizações', num: true, fmt: num },
  { key: 'carts', label: 'Carrinho', num: true, fmt: num },
  { key: 'purchases', label: 'Compras', num: true, fmt: num },
  { key: 'taxa_carrinho', label: 'Taxa de carrinho', num: true, fmt: rate },
  { key: 'taxa_compra', label: 'Taxa de compra', num: true, fmt: rate },
];

export const ViewsSection = ({ v, api, isPortal }) => {
  const mobile = useIsMobile();
  const lists = v.lists || {};
  return (
    <div className="space-y-4">
      {!mobile && v.scatter?.length > 0 && <Quadrant v={v} />}
      <div className="grid gap-4 md:grid-cols-3">
        <ShortList k="muito_visto_pouco_vendido" title="Muito visto, pouco vendido" rows={lists.muito_visto_pouco_vendido || []} api={isPortal ? null : api}
          metric={(r) => `${num(r.views)} visualizações · ${rate(r.taxa_compra)} de compra`} testId="list-muito-visto" />
        <ShortList k="pouco_visto_muito_convertido" title="Pouco visto, muito convertido" rows={lists.pouco_visto_muito_convertido || []}
          metric={(r) => `${rate(r.taxa_compra)} de compra · ${num(r.views)} visualizações`} testId="list-pouco-visto" />
        <ShortList title="Abandonados no carrinho" rows={lists.abandonados_carrinho || []}
          metric={(r) => `${num(r.carts)} no carrinho · ${num(r.purchases)} compras (${num(r.abandono)}% de abandono)`} testId="list-abandonados" />
      </div>
      <DataTable columns={COLS} rows={v.rows || []} testId="views-table" searchKey="nome" csvName={isPortal ? null : 'vistos-x-vendidos.csv'} initialSort={{ key: 'views', dir: 'desc' }} />
    </div>
  );
};
